import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { CreateTerritoryHttp, FindManyTerritoriesHttp, TerritoryDto } from '@mmscan/mmscan-api-types';
import type { CursorListReply, Reply, ResourceCreated } from '@mmscan/shared-types';
import type { Observable } from 'rxjs';

import { MMSCAN_API_BASE_URL } from '../mmscan-api.config';

export type {
  CreateTerritoryHttp,
  FindManyTerritoriesHttp,
  FindManyTerritoriesHttpInitial,
  TerritoryDto,
  UpdateTerritoryHttp,
} from '@mmscan/mmscan-api-types';

/**
 * Service for managing territories via mmscan-api REST endpoints
 *
 * @example
 * ```typescript
 * private readonly territoryService = inject(TerritoryService);
 *
 * getTerritories() {
 *   this.territoryService.findMany({ limit: 10, sortBy: 'createdAt', sortOrder: 'DESC' })
 *     .subscribe(result => console.log(result));
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TerritoryService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(MMSCAN_API_BASE_URL);

  private get url(): string {
    return `${this.baseUrl}/v1/territories`;
  }

  findMany(params: FindManyTerritoriesHttp): Observable<CursorListReply<TerritoryDto>> {
    return this.http.get<CursorListReply<TerritoryDto>>(this.url, { params });
  }

  findOne(id: string): Observable<Reply<TerritoryDto>> {
    return this.http.get<Reply<TerritoryDto>>(`${this.url}/${id}`);
  }

  create(body: CreateTerritoryHttp): Observable<ResourceCreated> {
    return this.http.post<ResourceCreated>(this.url, body);
  }
}
